import React from "react";
import { Badge } from "reactstrap";
import { TableTitle, UserAvatar } from "../../../../components/Component";
import Icon from "../../../../components/icon/Icon";
import { apiService } from "../../../../services/api";
import { APIServicePath } from "../../../../services/api/api-service-impl";
import { toPageInfo } from "../../../_common/DataConverter";
import moment from "moment";
import { findUpper, toCapitalize } from "../../../../utils/Functions";

export const nisccTableColumns = (onViewScreenshot) => {
  return [
    {
      name: <TableTitle title={"Ref ID"} />,
      id: "refId",
      selector: (row) => row?.refId ?? "",
      sortable: false,
    },
    {
      name: <TableTitle title={"Name"} />,
      id: "name",
      selector: (row) => row?.name ?? "",
      sortable: true,
      cell: (row) => (
        <UserAvatar
          size="sm"
          name={row?.name}
          image={row?.avatar}
          imageAlt={row?.name}
          text={row?.initials}
          subName={row?.role}
          className="my-2"
        />
      ),
    },
    {
      name: <TableTitle title={"NISCC No"} />,
      id: "nisccNo",
      selector: (row) => row?.nisccNo ?? "",
      sortable: true,
      cell: (row) => (
        <span className="text-primary cursor-pointer" onClick={row?.onViewNiscc}>
          {row?.nisccNo}
        </span>
      ),
    },
    {
      name: <TableTitle title={"NISCC Status"} />,
      id: "nisccStatus",
      selector: (row) => row?.nisccStatus ?? "",
      sortable: false,
      cell: (row) => (
        <Badge color={row?.nisccStatusColor ?? "success"} className="text-uppercase">
          {row?.nisccStatus}
        </Badge>
      ),
    },
    {
      name: <TableTitle title={"Last NISCC Check"} />,
      id: "lastNisccCheck",
      selector: (row) => row?.lastNisccCheck ?? "",
      sortable: true,
    },
    {
      name: <TableTitle title={""} align="center" />,
      right: true,
      width: "60px",
      cell: (row) => (
        <div
          className="cursor-pointer d-flex justify-content-start w-100 align-items-center h-100 text-primary"
          onClick={() => onViewScreenshot && onViewScreenshot(row)}
          title="View Screenshot"
        >
          <Icon name="eye" />
        </div>
      ),
    },
  ];
};

//***************************API's*********************************/
export function listNmcNisccCheckAPI(params, callback) {
  apiService
    .postCall(APIServicePath.carerNmcNisccCheckList, params)
    .then((data) => {
      let list = toNmcNisccCheckList(data?.list);
      let pageInfo = toPageInfo(data?.pageInfo);
      callback({ list, pageInfo, latestCheckTimestamp: data?.latestCheckTimestamp }, null);
    })
    .catch((error) => {
      callback(null, error);
    });
}

export function fetchCheckDatesAPI(params, callback) {
  apiService
    .postCall(APIServicePath.nmcNisccCheckDates, params)
    .then((data) => {
      callback(data, null);
    })
    .catch((error) => {
      callback(null, error);
    });
}

export function exportNmcNisccCheckAPI(params, callback) {
  apiService
    .postCall(APIServicePath.exportNmcNisccCheck, params, 5 * 60 * 1000)
    .then((data) => {
      callback(data, null);
    })
    .catch((error) => {
      callback(null, error);
    });
}

export function runScraperAPI(params, callback) {
  apiService
    .postCall(APIServicePath.carerRunScraper, params)
    .then((data) => {
      callback(data, null);
    })
    .catch((error) => {
      callback(null, error);
    });
}

//***************************Converters*********************************/
function toNmcNisccCheckList(data) {
  let list = [];
  data?.forEach((element) => {
    list.push(toNmcNisccCheckItem(element));
  });
  return list;
}

function toNmcNisccCheckItem(data) {
  const carer = data?.carer || {};
  const firstName = toCapitalize(carer?.firstName ?? "");
  const lastName = toCapitalize(carer?.lastName ?? "");
  const fullName = data?.name || `${firstName} ${lastName}`.trim();
  const initials = findUpper(firstName.charAt(0)) + findUpper(lastName.charAt(0));

  // Determine status color based on status value
  const getStatusColor = (status) => {
    if (!status) return "secondary";
    const statusUpper = status.toUpperCase();
    if (statusUpper.includes("ACTIVE") || statusUpper.includes("REGISTERED")) return "success";
    if (statusUpper.includes("EXPIRED")) return "danger";
    if (statusUpper.includes("INCORRECT") || statusUpper.includes("NOT FOUND")) return "secondary";
    if (statusUpper.includes("CONDITION")) return "warning";
    return "secondary";
  };

  // Format date from API response
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("DD-MM-YYYY");
  };
  // Format date and time for last check
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("DD-MM-YYYY HH:mm");
  };

  const pinType = data?.pinType || "";
  const isNiscc = pinType === "NISCC";

  return {
    id: data?.id,
    carerId: data?.carerId,
    refId: carer?.refNo ?? "",
    name: fullName,
    initials: initials || "NA",
    role: carer?.carerType?.name ?? "",
    avatar: carer?.carerImage?.imageUrl,
    colourHex: carer?.colourHex,
    // NISCC fields
    nisccNo: isNiscc ? (data?.nisccNmcPin ?? "") : "",
    nisccStatus: isNiscc ? (data?.status ?? "") : "",
    nisccStatusColor: isNiscc ? getStatusColor(data?.status) : "secondary",
    lastNisccCheck: isNiscc ? formatDateTime(data?.lastNisccNmcCheck) : "",
    screenshotUrl: data?.nisccNmcSs ?? "",
  };
}
